import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { dayjs } from 'shared/base';

import { AssetInventoryGridColDef } from '../../types';

interface Props {
  column: AssetInventoryGridColDef;
  onClose: () => void;
  onSave: (value: any) => void;
  open: boolean;
  resourceCount: number;
}

export function EditCellValueDialog({ column, onClose, onSave, open, resourceCount }: Props) {
  const [value, setValue] = useState<any>('');
  const { columnType, headerName: columnName } = column;

  const handleSave = () => {
    if (value) {
      onSave(value);
      setValue(''); // reset for next use
    }
  };

  return (
    <Dialog aria-labelledby={`${columnName}-dialog`} fullWidth={true} onClose={onClose} open={open}>
      <DialogTitle id={`${columnName}-dialog`}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Stack spacing={1}>
            <Typography variant="h6">{`Update "${columnName}" column`}</Typography>
            <Typography variant="body2">{`Changes will affect ${resourceCount} selected resources.`}</Typography>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {(() => {
          switch (columnType) {
            case 'DateTime': {
              return (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  dateFormats={{ keyboardDate: 'YYYY-MM-DD' }}
                >
                  <DatePicker
                    label="New value"
                    onChange={(newValue) => setValue(newValue?.format('YYYY-MM-DD'))}
                    sx={{ width: '100%' }}
                    value={value ? dayjs(value) : null}
                  />
                </LocalizationProvider>
              );
            }
            default: {
              return (
                <TextField
                  autoFocus={true}
                  fullWidth={true}
                  label="New value"
                  onChange={(e) => setValue(e.target.value)}
                  value={value}
                />
              );
            }
          }
        })()}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} size="medium" variant="outlined">
          Cancel
        </Button>
        <Button disabled={!value} onClick={handleSave} size="medium" variant="contained">
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
